/**
 * Starts the app in a page (part 11): the workspace on the browser's IndexedDB, Web Locks and BroadcastChannel, the
 * chapter editor in the page, the page's lifecycle events (part 11 log, table L), the service worker and the Home Screen
 * on iOS (part 40a), images put in the text (part 16b) and the interface. The browser's objects come in as an environment, so a test boots the whole app in
 * a simulated page.
 */
import { EditorSelection } from '@codemirror/state';
import { ChapterEditor } from '@cockatrice/editor';
import type { Bitmap } from '@cockatrice/layout';
import { openIndexedDbStore, requestPersistence } from '@cockatrice/storage';
import type { Channel, Locks, StorageManagerLike } from '@cockatrice/storage';
import { createEffect, createRoot, createSignal, on } from 'solid-js';
import { createTranslator } from './i18n/i18n.ts';
import type { Catalog, Translate } from './i18n/i18n.ts';
import type { Messages } from './i18n/en.ts';
import { editorKeys } from './shell/keys.ts';
import type { Platform } from './shell/keys.ts';
import { layoutMode } from './shell/layout.ts';
import { accountLinkOf, fragmentOf, readingFragment, readingOf, routeOf, verifyLinkOf } from './shell/route.ts';
import { Account } from './account/account.ts';
import type { AccountChannel } from './account/account.ts';
import { relayClient } from './account/client.ts';
import type { Fetch } from './account/client.ts';
import type { AccountConfig } from './account/config.ts';
import type { CredentialsContainer } from './account/passkey.boundary.ts';
import { openAccountStore } from './account/store.ts';
import { spaceEraser } from './account/erase.ts';
import { AccountManager } from './account/manager.ts';
import { randomOf } from './share/secrets.ts';
import { Sharing } from './share/sharing.ts';
import { Shared } from './shared/controller.ts';
import { isGuest } from './account/status.ts';
import type { PdfBook } from '@cockatrice/layout';
import { openSharedStore } from './shared/idb.ts';
import { INVITE_ROUTE } from './share/invite-secrets.ts';
import type { SharingOptions } from './share/sharing.ts';
import { backupKdf, deriveInWorker } from './kdf/host.ts';
import type { KdfWorker } from './kdf/host.ts';
import { sealInWorker } from './share/seal.ts';
import type { SealWorker } from './share/seal.ts';
import { BundleController } from './bundle/controller.ts';
import type { BundleWorker } from './bundle/controller.ts';
import { BackupController } from './backup/controller.ts';
import type { BackupWorker } from './backup/controller.ts';
import { openBackupLog } from './backup/db.ts';
import { EpubController } from './epub/controller.ts';
import type { EpubWorker } from './epub/controller.ts';
import { DocxController } from './docx/controller.ts';
import type { DocxWorker } from './docx/controller.ts';
import { DOCX_TYPE } from './docx/protocol.ts';
import { ExportController } from './export/controller.ts';
import { CountController } from './count/controller.ts';
import { FindController } from './find/controller.ts';
import { FindHost } from './find/host.ts';
import type { FindWorker } from './find/host.ts';
import { comparePicture, importImage } from './images/importer.ts';
import type { ImageWorker, ImportResult } from './images/importer.ts';
import { ImageInserter } from './images/insert.ts';
import { ImportController } from './import/controller.ts';
import { BundleImportController } from './bundle-import/controller.ts';
import { ImportSandbox } from './import/host.ts';
import { ReimportController } from './reimport/controller.ts';
import { runMerge } from './reimport/merge-run.ts';
import type { ImportWorker } from './import/host.ts';
import { PreviewController } from './preview/controller.ts';
import { PwaController } from './pwa/controller.ts';
import type { ContainerLike } from './pwa/controller.ts';
import { HomeScreen, storedChoice } from './pwa/home-screen.ts';
import type { ChoiceStorage } from './pwa/home-screen.ts';
import { InstallPrompt } from './pwa/install-prompt.ts';
import type { LayoutConnection } from './preview/controller.ts';
import type { UnsavedStorage } from './shell/unsaved.ts';
import { FIRST_SPACE, spaceOf } from './shell/space.ts';
import { sharedWorker } from './preview/shared.ts';
import { Workspace } from './shell/workspace.ts';
import type { EditorHandle, EditorRequest } from './shell/workspace.ts';
import { App } from './screens/app.ts';
import { mount, view } from './ui/dom.ts';
import { InterfaceState } from './screens/shell.ts';
import type { Shell } from './screens/shell.ts';

/**
 * The name of the channel every tab of the app opens (part 06: "use here" and release requests): the first store's; an
 * account's own store has its own (part 36a, table BK).
 */
export const CHANNEL = FIRST_SPACE.channel;

/** The browser objects the app uses. */
export interface BootEnvironment {
  readonly window: Window;
  readonly root: HTMLElement;
  readonly indexedDB: IDBFactory;
  readonly locks: Locks | undefined;
  readonly storage: StorageManagerLike | undefined;
  readonly channel: (name: string) => Channel;
  readonly random: (bytes: Uint8Array<ArrayBuffer>) => void;
  /** The interface's language and its messages (decision 34: English at launch). */
  readonly locale: string;
  readonly catalog: Messages;
  /**
   * Starts a layout worker, shared by the preview (part 15) and the PDF export (part 21); called only when one of them
   * first has something to ask.
   */
  readonly layoutWorker: (store: string) => LayoutConnection;
  /**
   * Starts a bundle worker (part 24a): one for each bundle, ended when it is done. Each worker that reads the book store
   * is started with the store's name (part 36a, table BK10).
   */
  readonly bundleWorker: (store: string) => BundleWorker;
  /** Starts a backup worker (part 24e): one for each run, ended when it is done. */
  readonly backupWorker: (store: string) => BackupWorker;
  /**
   * Part 41b: starts a KDF worker for the backup lock (its key, a locked backup opened), one for each, ended when it
   * answers; with accounts off too.
   */
  readonly kdfWorker: () => KdfWorker;
  /** Part 24e, table P: the folder picker (`showDirectoryPicker`), or null where the browser has none. */
  readonly pickFolder: (() => Promise<unknown>) | null;
  /** Part 24e, M7: the share sheet with a file, or null where files can't be shared. */
  readonly shareFile: ((file: File) => Promise<void>) | null;
  /** Starts an EPUB worker (part 20): one for each export, ended when it is done. */
  readonly epubWorker: (store: string) => EpubWorker;
  /** Starts a DOCX worker (part 22): one for each export, ended when it is done. */
  readonly docxWorker: (store: string) => DocxWorker;
  /** Starts an image worker (part 16a): one for each image, ended when it is done. */
  readonly imageWorker: () => ImageWorker;
  /** Starts an import worker (parts 17, 18): one for each import, ended when it is done. */
  readonly importWorker: () => ImportWorker;
  /** Starts a find worker (part 19a): one kept between searches, another after one is ended (table T). */
  readonly findWorker: () => FindWorker;
  /** SHA-256 (WebCrypto's digest): an image's hash (part 16a). */
  readonly digest: (bytes: Uint8Array) => Promise<Uint8Array>;
  /** Puts a drawn page's pixels on a canvas. */
  readonly present: (canvas: HTMLCanvasElement, bitmap: Bitmap) => void;
  /** Watches an element's width (a `ResizeObserver`); returns what stops it. */
  readonly watchSize: (element: HTMLElement, changed: (width: number, height: number) => void) => () => void;
  /**
   * An object URL for a file's bytes of a media type, or a file (`URL.createObjectURL` of a blob), and what lets it go
   * (parts 21, 24a).
   */
  readonly objectUrl: (bytes: Uint8Array<ArrayBuffer> | Blob, type: string) => string;
  readonly revokeUrl: (url: string) => void;
  /** The page's service worker container (`navigator.serviceWorker`); undefined without one (part 40a, U1). */
  readonly serviceWorkers: ContainerLike | undefined;
  /** The service worker's script URL under the app's Trusted Types policy; null on the dev server (U1). */
  readonly serviceWorkerUrl: (() => string) | null;
  /** Whether the page runs on iOS outside the Home Screen app (part 40a, H2). */
  readonly outsideHomeScreen: boolean;
  /** Where the Home Screen choice is kept (`localStorage`; H6: getting it may throw). */
  readonly choiceStorage: () => ChoiceStorage;
  /** Where a page keeps its typing not stored yet as it goes (`localStorage`; fix-next 1: getting it may throw). */
  readonly unsavedStorage: () => UnsavedStorage;
  /** Part 36c, FX4: copies text to the clipboard (`clipboardCopier`), and the print dialog of what is marked (`printer`). */
  readonly copy: (text: string) => Promise<boolean>;
  readonly print: () => void;
  /** Part 38b, IW1: the device's share sheet (`navigator.share`): true once shared; null where the browser has none. */
  readonly shareSheet: ((data: { readonly title: string; readonly text: string; readonly url: string }) => Promise<boolean>) | null;
  /** Part 36a: what the account takes when the build names a relay; absent: accounts off (AC1, C6). */
  readonly account?: AccountEnvironment;
}

/** Part 36a: the account's browser objects, given only when the build names a relay. */
export interface AccountEnvironment {
  readonly config: AccountConfig;
  /** The browser's `fetch`, for the relay alone (C6: the CSP's `connect-src` names it). */
  readonly fetch: Fetch;
  /** Starts a KDF worker (part 25a): one for each derivation, ended when it answers. */
  readonly kdfWorker: () => KdfWorker;
  /** Part 38b, DI6: starts a sealing worker: one for each item of an invite's copy, ended when it answers. */
  readonly sealWorker: () => SealWorker;
  readonly subtle: SubtleCrypto;
  /** `navigator.credentials` (a passkey's second step), or undefined. */
  readonly credentials: CredentialsContainer | undefined;
}

/**
 * Part 38b: what the invites run on in the page: the workspace's invite store, the page's origin (where invite links
 * point), the sealing in a worker of its own (DI6; none with accounts off) and the window's timers.
 */
export function inviteEnvironment(
  store: SharingOptions['invites']['store'],
  page: Pick<Window, 'setTimeout' | 'clearTimeout'> & { readonly location: Pick<Location, 'origin'> },
  account: Pick<AccountEnvironment, 'sealWorker'> | undefined,
): SharingOptions['invites'] {
  return {
    store,
    appOrigin: page.location.origin,
    seal: (job) => (account === undefined ? Promise.resolve(null) : sealInWorker(account.sealWorker, job)),
    timers: {
      set: (run, ms) => page.setTimeout(run, ms),
      clear: (handle) => {
        page.clearTimeout(handle);
      },
    },
  };
}

/** The name of the channel the account's tabs tell each other on (part 36a, CL14). */
export const ACCOUNT_CHANNEL = 'cockatrice-account';

/** Which modifier `Mod` is on this device (K3): Cmd on Apple platforms. */
export function platformOf(navigator: { readonly platform: string; readonly userAgent: string }): Platform {
  return /Mac|iPhone|iPad|iPod/u.test(navigator.platform) || /Macintosh|iPhone|iPad/u.test(navigator.userAgent) ? 'mac' : 'other';
}

/** A fresh ID: 128 random bits as 32 lowercase hex digits (part 06, R3). */
export function randomId(random: (bytes: Uint8Array<ArrayBuffer>) => void): string {
  const bytes = new Uint8Array(16);
  random(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** The editor handle the workspace drives, over part 09's editor. */
function handle(editor: ChapterEditor): EditorHandle {
  return {
    get parsed() {
      return editor.parsed;
    },
    get cursor() {
      return editor.view.state.selection.main.head;
    },
    get status() {
      return editor.status;
    },
    flush: () => editor.flush(),
    close: () => editor.close(),
    pause: (paused) => {
      editor.pause(paused);
    },
    setAutoConversion: (on) => {
      editor.setAutoConversion(on);
    },
    setParagraphLook: (look) => {
      editor.setParagraphLook(look);
    },
    resave: () => {
      editor.resave();
    },
    markImage: (at) => editor.markImage(at),
    insertImage: (mark, markup) => editor.insertImage(mark, markup),
    releaseImage: (mark) => {
      editor.releaseImage(mark);
    },
    showMatches: (ranges) => {
      editor.showMatches(ranges);
    },
    showNotes: (marks) => {
      editor.showNotes(marks);
    },
    goTo: (range) => {
      editor.goTo(range);
    },
    get matches() {
      return editor.matches;
    },
    get currentMatch() {
      return editor.currentMatch;
    },
    get selection() {
      return editor.selection;
    },
    selectMatch: (range) => {
      editor.selectMatch(range);
    },
    replaceText: (expected, text) => editor.replaceText(expected, text),
  };
}

/** Boots the app into `env.root`; returns what stops it (tests). */
/** Part 38c: how often the shared books are checked while the page is up (a removal or an end shown within this). */
export const SHARED_CHECK_MS = 5 * 60_000;

export function boot(env: BootEnvironment): () => void {
  const { window } = env;
  const t: Translate<Messages> = createTranslator<Messages & Catalog>(env.catalog, env.locale);
  const platform = platformOf(window.navigator);
  const reserved = editorKeys(platform);
  const host = window.document.createElement('div');
  host.className = 'editor-host';
  let editor: ChapterEditor | null = null;
  const schedule = (ms: number, run: () => void): (() => void) => {
    const timer = window.setTimeout(run, ms);
    return () => {
      window.clearTimeout(timer);
    };
  };
  // Part 36a, table BK: the names of the store the page opened its books on (the first store's until it opens one).
  const [page, setPage] = createSignal(FIRST_SPACE);
  const store = (): string => page().books;
  const layout = sharedWorker(() => env.layoutWorker(store()));
  const preview = new PreviewController({ connect: layout, schedule });
  const delay = (ms: number): Promise<void> =>
    new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  // Part 36a: the account, before the workspace that asks it whether the books may change (table RO).
  const intervals: number[] = [];
  const accountEnv = env.account;
  // Part 38c: the books shared with the writer, told of each typed password and each logout (made right after the
  // account, which calls them only on a login or a logout, long after this).
  const account: Account = new Account({
    setup:
      accountEnv === undefined
        ? null
        : {
            config: accountEnv.config,
            client: relayClient({ origin: accountEnv.config.relay, fetch: accountEnv.fetch, delay }),
            subtle: accountEnv.subtle,
            derive: (job) => deriveInWorker(accountEnv.kdfWorker, job),
          },
    openStore: () => openAccountStore(env.indexedDB),
    random: (bytes) => {
      env.random(bytes);
      return bytes;
    },
    now: () => Date.now(),
    monotonic: () => window.performance.now(),
    every: (ms, tick) => {
      intervals.push(window.setInterval(tick, ms));
    },
    channel: (): AccountChannel => env.channel(ACCOUNT_CHANNEL),
    online: () => window.navigator.onLine,
    // Part 36c, FD8, FD9: a deleted account's storage, by its shelf's names, deleted at a start.
    namesOf: (shelf) => {
      const { books, backups, unsaved } = spaceOf(shelf);
      return { books, backups, unsaved };
    },
    deleteStores: spaceEraser(env.indexedDB, env.unsavedStorage),
    unlocked: (event) => sharedBooks.unlocked(event),
    loggedOut: (who) => sharedBooks.loggedOut(who),
  });
  const sharedBooks: Shared = new Shared({
    account,
    openStore: () => openSharedStore(env.indexedDB),
    subtle: accountEnv?.subtle ?? window.crypto.subtle,
    random: (bytes) => {
      env.random(bytes);
      return bytes;
    },
    now: () => Date.now(),
  });
  // Part 36c: the account page's lists and actions (part 38c: the keyring's side of a change of P and of an Accept).
  const manager = new AccountManager(account, () => Date.now(), sharedBooks);
  const workspace = new Workspace({
    // BK7: the account's shelf once read, the same one for the page's life.
    openStore: async () => openIndexedDbStore({ factory: env.indexedDB, name: setPage(spaceOf(await account.openShelf())).books }),
    locks: env.locks,
    channel: () => env.channel(page().channel),
    delay,
    now: () => Date.now(),
    newId: () => randomId(env.random),
    presetName: (id) => t(`format.preset.${id}`),
    templateCopyName: (name) => t('format.copyOf', { name }),
    digest: env.digest,
    zone: (at) => -new Date(at).getTimezoneOffset(),
    platform,
    reserved,
    persist: () => {
      void requestPersistence(env.storage);
    },
    openEditor: (request: EditorRequest) => {
      const made: ChapterEditor = new ChapterEditor({
        parent: host,
        text: request.text,
        language: request.language,
        save: request.save,
        schedule,
        now: () => window.performance.now(),
        onStatus: request.onStatus,
        autoConversion: request.autoConversion,
        paragraphLook: request.paragraphLook,
        label: t('editor.label'),
        // E10: the preview follows the text and the cursor of the handle the workspace holds (part 15). The editor
        // reports nothing while it is made, so the handle exists by the first report.
        onUpdate: (update) => {
          preview.edited(opened, update);
          // Part 19e: the selection bar's Private note shows while text is chosen.
          ui.setTextChosen(!made.view.state.selection.main.empty);
          // Part 19a, F9: the open chapter's matches searched again once the writer pauses; part 19b, D2: its words too.
          if (update.changes.length > 0) {
            finder.edited();
            counter.edited();
          }
        },
        // Part 16b, I2, I3: image files pasted or dropped in the text.
        onImages: (files, at) => {
          images.insert(files, at);
        },
      });
      editor = made;
      const opened = handle(made);
      return opened;
    },
    soon: (run) => {
      window.setTimeout(run, 0);
    },
    untitledBook: () => t('book.untitled'),
    // Decision 14: cold start ends with the text taking typing.
    onReady: () => {
      shell.focusEditor();
      // Part 24e, T1: once the workspace is ready (the device's ID known).
      backups.start();
    },
    unsavedStorage: env.unsavedStorage,
    permit: () => account.permit(),
    space: page,
    // BK2: the empty store shown with nobody's books gets no book of its own.
    // Part 38c, deep-fix r5: a guest writes no books (decision 75), so its store gets no first book to show as its own.
    firstBook: () => page().books !== spaceOf({ kind: 'none' }).books && !isGuest(account.status()),
  });
  // Part 38d: the reading links, over the workspace's store and the account's relay.
  const sharing = new Sharing({
    account,
    store: workspace.shareStore(),
    random: randomOf(env.random),
    now: Date.now,
    // Part 38b: the invites' copy, sealed in a worker of its own (DI6), pushed a pause after each save.
    invites: inviteEnvironment(workspace.inviteStore(), window, accountEnv),
  });
  const stopSaves = workspace.onSaved((bookId) => {
    sharing.saved(bookId);
  });
  // Part 38c: on a shared book's read view, the exports take that book (when its writer allows it, GP6).
  const sharedBook = (): string | null => (ui.route() === 'reading' ? ui.reading() : null);
  const exportBook = (): Promise<PdfBook | null> => {
    const invite = sharedBook();
    return invite === null ? workspace.exportBook() : sharedBooks.pdfBook(invite, t('book.untitled'));
  };
  const exporter = new ExportController({
    connect: layout,
    readBook: exportBook,
    objectUrl: (bytes) => env.objectUrl(bytes, 'application/pdf'),
    revokeUrl: env.revokeUrl,
  });
  // Part 20: the book as the PDF reads it (table N), made into an EPUB in a worker of its own.
  const epub = new EpubController({
    readBook: exportBook,
    worker: () => env.epubWorker(store()),
    now: () => Date.now(),
    objectUrl: (file) => env.objectUrl(file, 'application/epub+zip'),
    revokeUrl: env.revokeUrl,
  });
  // Part 22: the book with its chapters' IDs and each one's base pinned (table PN), made into a DOCX in a worker of its own.
  const docx = new DocxController({
    // Part 38c: a shared book's chapters have no snapshot on this device to pin (table PN's `kept` false).
    readBook: (modified) => {
      const invite = sharedBook();
      return invite === null ? workspace.docxBook(modified) : sharedBooks.docxBook(invite, t('book.untitled'));
    },
    worker: () => env.docxWorker(store()),
    now: () => Date.now(),
    objectUrl: (file) => env.objectUrl(file, DOCX_TYPE),
    revokeUrl: env.revokeUrl,
  });
  const bundler = new BundleController({
    request: () => workspace.bundleRequest(),
    worker: () => env.bundleWorker(store()),
    objectUrl: (file) => env.objectUrl(file, 'application/zip'),
    revokeUrl: env.revokeUrl,
    now: () => Date.now(),
  });
  // Part 41b: the backup lock's Argon2id, in a KDF worker of its own each time.
  const lockKdf = backupKdf(env.kdfWorker);
  // Part 24e: each changed book's bundle to a folder once a day, or a reminder where there is no folder.
  const backups = new BackupController({
    openLog: (gone) => openBackupLog(env.indexedDB, { gone, name: page().backups }),
    worker: () => env.backupWorker(store()),
    locks: env.locks,
    pickFolder: env.pickFolder,
    share: env.shareFile,
    device: () => workspace.deviceId(),
    openBook: () => workspace.openBookId(),
    untitled: () => t('book.untitled'),
    now: () => Date.now(),
    zone: (at) => -new Date(at).getTimezoneOffset(),
    schedule,
    hidden: () => window.document.visibilityState === 'hidden',
    objectUrl: (file) => env.objectUrl(file, 'application/zip'),
    revokeUrl: env.revokeUrl,
    lockKeys: { backupKey: lockKdf.backupKey, random: env.random },
  });
  // Part 16b, table I: each file through part 16a's worker and the book's library, into the open chapter's editor.
  const importPicture = (bookId: string, file: Blob): Promise<ImportResult> =>
    importImage(bookId, file, 'text', {
      worker: env.imageWorker,
      add: (id, image) => workspace.addImage(id, image),
      setTimer: (run, ms) => window.setTimeout(run, ms),
      clearTimer: (timer) => {
        window.clearTimeout(Number(timer));
      },
    });
  const images = new ImageInserter({
    importFile: importPicture,
    place: () => workspace.imagePlace(),
    untitled: () => t('images.untitled'),
  });
  const timers = {
    setTimer: (run: () => void, ms: number) => window.setTimeout(run, ms),
    clearTimer: (timer: unknown) => {
      window.clearTimeout(Number(timer));
    },
  };
  // Part 18, table V: a file read in part 17's sandbox, reviewed, its pictures through part 16a's pipeline, one write.
  const sandbox = new ImportSandbox({
    worker: env.importWorker,
    ...timers,
    now: () => window.performance.now(),
    hidden: () => window.document.visibilityState === 'hidden',
    onVisibility: (listener) => {
      window.document.addEventListener('visibilitychange', listener);
      return () => {
        window.document.removeEventListener('visibilitychange', listener);
      };
    },
  });
  const importer = new ImportController({
    run: (file, format, request) => sandbox.run(file, format, request),
    addPicture: (bookId, file) => importImage(bookId, file, 'text', { worker: env.imageWorker, add: (id, image) => workspace.addImage(id, image), ...timers }),
    commit: (request) => workspace.importBook(request),
    newId: () => randomId(env.random),
  });
  // Part 24c, tables L and S: a bundle checked in the same sandbox, reviewed, then read again batch by batch into a hidden
  // book that appears whole or not at all; part 24d: or staged as an update of the book here, applied on the writer's word.
  const restorer = new BundleImportController({
    run: (file, format, request) => sandbox.run(file, format, request),
    read: (file, manifest, request) => sandbox.read(file, manifest, request),
    bookHere: (bookId) => workspace.bookHere(bookId),
    begin: (bookId) => workspace.beginImport(bookId),
    listImported: () => workspace.listImported(),
    readForUpdate: (read) => workspace.readForUpdate(read),
    applyUpdate: (running, request, handover) => workspace.applyUpdate(running, request, handover),
    zone: (at) => -new Date(at).getTimezoneOffset(),
    mergeSources: (chapters, signal) => runMerge(() => env.docxWorker(store()), { kind: 'merge-sources', chapters }, () => undefined, signal),
    encodePicture: (bytes, add) => importImage('', new Blob([Uint8Array.from(bytes)]), 'text', { worker: env.imageWorker, add: (_bookId, image) => add(image), ...timers }),
    digest: env.digest,
    newId: () => randomId(env.random),
    now: () => Date.now(),
    words: {
      recoveredText: () => t('bundleImport.recoveredText'),
      recoveredPictures: () => t('bundleImport.recoveredPictures'),
      picture: (n) => t('bundleImport.picture', { n }),
      untitled: () => t('book.untitled'),
    },
  });
  // Part 23b, table V′: a Word file read in the same sandbox, its pictures decided, merged in a DOCX worker, reviewed, one write.
  const reimporter = new ReimportController({
    run: (file, request) => sandbox.run(file, 'docx', request),
    openBook: () => workspace.openBookId(),
    book: () => workspace.reimportBook(),
    base: (bookId, chapterId, snapshotId) => workspace.exportBase(bookId, chapterId, snapshotId),
    pictures: {
      stored: (bookId, id) => workspace.storedImage(bookId, id),
      byHash: (bookId, sha256) => workspace.imageByHash(bookId, sha256),
      digest: env.digest,
      same: (file, stored) => comparePicture(file, stored, { worker: env.imageWorker, ...timers }),
    },
    merge: (job, onProgress, signal) => runMerge(() => env.docxWorker(store()), job, onProgress, signal),
    addPicture: (bookId, file) => importImage(bookId, file, 'text', { worker: env.imageWorker, add: (id, image) => workspace.addImage(id, image), ...timers }),
    commit: (request) => workspace.reimport(request),
  });
  // Part 19a, table T: the find worker, kept between searches; and the panel's logic over it and the workspace.
  const findHost = new FindHost({
    worker: env.findWorker,
    ...timers,
    hidden: () => window.document.visibilityState === 'hidden',
    onVisibility: (listener) => {
      window.document.addEventListener('visibilitychange', listener);
      return () => {
        window.document.removeEventListener('visibilitychange', listener);
      };
    },
  });
  const finder = new FindController({
    place: {
      bookId: () => workspace.openBookId(),
      chapterId: () => workspace.chapterId(),
      editor: () => workspace.editor(),
      // Part 36a, RO: a replace changes the book, so it asks whether the books may change.
      editable: () => workspace.writable(),
      texts: (scope) => workspace.findTexts(scope),
      openChapter: (chapterId) => workspace.openChapterById(chapterId),
      replaceAll: (scope, compute) => workspace.replaceAll(scope, compute),
      undoReplaceAll: () => workspace.undoReplaceAll(),
    },
    run: (request) => findHost.run(request),
    schedule,
  });
  // Part 19b: word counts in the same worker (principle 2), today's words and the goals in this device's settings.
  const counter = new CountController({
    place: {
      bookId: () => workspace.openBookId(),
      chapterId: () => workspace.chapterId(),
      editor: () => workspace.editor(),
      texts: () => workspace.findTexts('book'),
      writing: (bookId) => workspace.writing(bookId),
      changeWriting: (bookId, change) => {
        void workspace.changeWriting(bookId, change);
      },
    },
    run: (request) => findHost.run(request),
    schedule,
    now: () => Date.now(),
    zone: (at) => -new Date(at).getTimezoneOffset(),
  });
  const reload = (): void => {
    window.location.reload();
  };
  // Part 40a: the update reloads only once the workspace has stored the typing (table N).
  const pwa = new PwaController({ container: env.serviceWorkers, url: env.serviceWorkerUrl, settle: () => workspace.settle(), reload, now: () => Date.now() });
  const installer = new InstallPrompt();
  const homeScreen = new HomeScreen(env.outsideHomeScreen, storedChoice(env.choiceStorage));
  // Y1: the signal changes only when the width crosses the line, so a resize costs one comparison.
  const [mode, setMode] = createSignal(layoutMode(window.innerWidth));
  const [pageVisible, setPageVisible] = createSignal(window.document.visibilityState === 'visible');
  const resized = (): void => {
    setMode(layoutMode(window.innerWidth));
  };
  window.addEventListener('resize', resized);
  // Part 11d, R1, R2: the page the address names (a launch names none: home); Back, Forward and a typed address follow.
  // Part 11f, WT1: the rail starts collapsed to the chapters' numbers (the design's default).
  const ui = new InterfaceState(false, routeOf(window.location.hash));
  // Part 36a, SU6, C5: a verify link's token is taken out of the address and the history at once, the home page shown;
  // part 36c, LK, C12: so is every other mailed link's, its card shown.
  const takeLink = (): void => {
    // Part 38c, GP1: an invite link's fragment (its claim secret and person key) out of the address and the history at
    // once, kept on this device for the claim; Shared with me shows what became of it.
    if (window.location.hash.startsWith(INVITE_ROUTE)) {
      const hash = window.location.hash;
      window.history.replaceState(null, '', fragmentOf('shared'));
      ui.setRoute('shared');
      void sharedBooks.takeLink(hash);
      return;
    }
    ui.setReading(readingOf(window.location.hash));
    const verify = verifyLinkOf(window.location.hash);
    const other = accountLinkOf(window.location.hash);
    // A link's kind and token ('' for one that holds none), whichever the fragment is.
    const link = verify !== null ? { kind: 'verify' as const, token: verify === 'bad' ? '' : verify.token } : other === null ? null : { kind: other.kind, token: other.token ?? '' };
    // With accounts off no link can have been sent; its address shows the home page (R2).
    if (link === null || accountEnv === undefined) return;
    window.history.replaceState(null, '', fragmentOf('home'));
    ui.setRoute('home');
    if (link.kind === 'verify') ui.setVerifyToken(link.token);
    else ui.setLinkToken(link.token);
    ui.setAccountStep(link.kind);
  };
  takeLink();
  const followAddress = (): void => {
    ui.setRoute(routeOf(window.location.hash));
    takeLink();
  };
  window.addEventListener('hashchange', followAddress);
  // Part 36a, BK8: the account left the page's shelf (logged out, or another account in this tab or another): once a
  // card the writer is on is done (a second step's codes left), the typing is stored and the page opens the new shelf.
  // Typing that can be neither stored nor kept in the page's record keeps the page, read-only, until the writer reloads
  // it (BK12: the browser then asks before the typing goes).
  let leavingShelf = false;
  const stopShelf = createRoot((dispose) => {
    createEffect(() => {
      if (!account.moved() || ui.accountStep() !== null || leavingShelf) return;
      leavingShelf = true;
      void workspace.leaveWith(reload);
    });
    // Part 38c, LO3, LO4: another tab's logout (or another account there) takes the shared books from this page too.
    createEffect(
      on(account.status, () => {
        void sharedBooks.accountChanged();
      }),
    );
    // BK2: a store with no book (the empty one shown with nobody's books) has nothing to write in: the library shows.
    createEffect(() => {
      if (workspace.screen() !== 'workspace' || workspace.book() !== null || (ui.route() !== 'write' && ui.route() !== 'format')) return;
      window.history.replaceState(null, '', fragmentOf('home'));
      ui.setRoute('home');
    });
    return dispose;
  });
  const shell: Shell = {
    workspace,
    account,
    credentials: accountEnv?.credentials,
    manager,
    sharing,
    shared: sharedBooks,
    read: (invite) => {
      ui.setReading(invite);
      window.history.pushState(null, '', readingFragment(invite));
      ui.enter('reading');
    },
    fileUrl: env.objectUrl,
    revokeUrl: env.revokeUrl,
    copy: env.copy,
    shareSheet: env.shareSheet,
    print: env.print,
    // Part 41b, E11–E13: a locked backup picked for import, opened in a KDF worker of its own.
    openBackup: lockKdf.openBackup,
    t,
    locale: env.locale,
    platform,
    reserved,
    mode,
    ui,
    go: (route) => {
      if (route === ui.route()) return;
      window.history.pushState(null, '', fragmentOf(route));
      ui.enter(route);
    },
    back: () => {
      window.history.back();
    },
    now: () => Date.now(),
    zone: (at) => -new Date(at).getTimezoneOffset(),
    reload,
    editorHost: host,
    focusEditor: () => {
      if (editor?.view.dom.isConnected === true) editor.view.focus();
    },
    placeCaret: (x, y, extend) => {
      if (editor?.view.dom.isConnected !== true) return false;
      const { view: text } = editor;
      // Not precise: a point beside, above or below the text still gives the nearest place in it.
      const head = text.posAtCoords({ x, y }, false);
      const anchor = extend ? text.state.selection.main.anchor : head;
      text.focus();
      text.dispatch({ selection: EditorSelection.range(anchor, head) });
      return true;
    },
    preview,
    exporter,
    bundler,
    backups,
    epub,
    docx,
    addCover: (bookId, file) => importImage(bookId, file, 'cover', { worker: env.imageWorker, add: (id, image) => workspace.addImage(id, image), ...timers }),
    images,
    importPicture,
    importer,
    reimporter,
    restorer,
    finder,
    counter,
    pwa,
    installer,
    homeScreen,
    present: env.present,
    pageVisible,
    pixelRatio: () => window.devicePixelRatio,
    watchSize: env.watchSize,
  };
  // Table L: the page's lifecycle.
  const visibility = (): void => {
    const visible = window.document.visibilityState === 'visible';
    setPageVisible(visible);
    if (visible) {
      workspace.visible();
      // Part 38b, deep-fix r6: a copy a problem stopped is tried again.
      sharing.visible(workspace.book()?.id);
      pwa.visible();
      backups.visible();
      account.visible();
      // Part 38c, CC: shared books checked again (at most once a minute).
      void sharedBooks.refresh();
    } else {
      workspace.hidden();
      account.hidden();
      // Part 38b, DI6: a copy waiting for its pause goes now.
      sharing.hidden();
    }
  };
  const online = (): void => {
    sharing.online(workspace.book()?.id);
    void sharedBooks.refresh();
  };
  const pageHide = (): void => {
    workspace.pageHide();
  };
  const pageShow = (event: PageTransitionEvent): void => {
    // Deep-fix r7, BF3: the account read again, writing held meanwhile, before the workspace reconnects.
    if (event.persisted) account.resumed();
    workspace.pageShow(event.persisted);
  };

  window.document.addEventListener('visibilitychange', visibility);
  window.addEventListener('online', online);
  window.addEventListener('pagehide', pageHide);
  window.addEventListener('pageshow', pageShow);
  const stopInstaller = installer.start((type, listener) => {
    window.addEventListener(type, listener);
    return () => {
      window.removeEventListener(type, listener);
    };
  });
  const dispose = mount(() => view(App, shell), env.root);
  void workspace.start();
  // Part 38c: the shared books once the account is read; checked every few minutes while the page is up (CC9's end
  // dates on this clock, a removal at the next check).
  void account.start().then(() => sharedBooks.start());
  intervals.push(window.setInterval(() => void sharedBooks.refresh(), SHARED_CHECK_MS));
  pwa.start();
  return () => {
    dispose();
    stopShelf();
    window.removeEventListener('resize', resized);
    window.removeEventListener('hashchange', followAddress);
    window.document.removeEventListener('visibilitychange', visibility);
    window.removeEventListener('online', online);
    stopSaves();
    window.removeEventListener('pagehide', pageHide);
    window.removeEventListener('pageshow', pageShow);
    stopInstaller();
    pwa.stop();
    workspace.pageHide();
    exporter.stop();
    epub.stop();
    docx.stop();
    bundler.stop();
    backups.stop();
    preview.stop();
    findHost.stop();
    for (const interval of intervals) window.clearInterval(interval);
  };
}
